import { Stack, ToggleButton, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { number, object } from 'yup';

import { useLocale } from '@frontend/hooks';

import { CustomAmountField } from './custom-amount-field.component';

export interface AmountSelectorProps {
  values: number[];
  customAmountEnabled?: boolean | undefined;
  onAmountSelected: (amount: number) => void;
}

export const AmountSelector = (props: AmountSelectorProps) => {
  const [selected, setSelected] = useState<number | undefined>(
    props.values[0]!,
  );
  const theme = useTheme();
  const { formatters } = useLocale();

  const handleSelection = (amount: number) => {
    setSelected(amount);
    props.onAmountSelected(amount);
  };

  const handleCustomAmount = (amount: number | undefined) => {
    if (amount === undefined) {
      return;
    }
    setSelected(undefined);
    props.onAmountSelected(amount);
  };

  useEffectOnce(() => {
    handleSelection(selected!);
  });

  const validationSchema = useMemo(
    () =>
      object<{ customAmount: number | string }>({
        customAmount: number()
          .transform((value) => (value === '' ? value : Number(value)))
          .moreThan(0)
          .positive(),
      }),
    [],
  );

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${theme.spacing(12)}, 1fr))`,
          gap: 2,
        }}
      >
        {props.values.map((option, index) => (
          <ToggleButton
            value={option}
            key={index + option}
            selected={selected === option}
            onClick={() => {
              handleSelection(option);
            }}
            size="large"
            sx={{
              minHeight: '64px',
              fontSize: '20px',
              textTransform: 'lowercase',
              borderRadius: `${theme.shape.borderRadius}px`,
            }}
          >
            {formatters.currency(option, true)}
          </ToggleButton>
        ))}
      </Stack>

      {props.customAmountEnabled && (
        <Formik<{ customAmount: number | string }>
          initialValues={{
            customAmount: '',
          }}
          validationSchema={validationSchema}
          validateOnChange
          onSubmit={() => {}}
        >
          {() => {
            return (
              <Form>
                <CustomAmountField
                  selectedAmountOption={selected}
                  onCustomAmountChange={(amount) => handleCustomAmount(amount)}
                />
              </Form>
            );
          }}
        </Formik>
      )}
    </Stack>
  );
};
